import { useEffect, useState, useRef, useCallback } from 'react';
import { X, Bell, Clock, ChevronLeft, ChevronRight } from 'lucide-react';
import { getNotification, ReadNotification } from '../../redux/actions/notifyMe';
import { useDispatch } from 'react-redux';
import { useUser } from 'contexts';
import { useColorMode } from '@chakra-ui/react';

const NotificationModal = ({ onClose, NotificationData, Paginationdata }) => {
   const { colorMode } = useColorMode();
   const isDark = colorMode === 'dark';
   const { user } = useUser();
   const dispatch = useDispatch();
   const [currentPage, setCurrentPage] = useState(1);
   const [limit] = useState(10);
   const modalRef = useRef(null);
   const closeCalledRef = useRef(false);

   useEffect(() => {
      dispatch(ReadNotification({ companyId: user?.loginhistory?.companyId }));
      dispatch(
         getNotification({
            companyId: user?.loginhistory?.companyId,
            page: currentPage,
            limit: limit,
         }),
      );
   }, [dispatch, user?.loginhistory?.companyId, currentPage, limit]);

   // Memoize close function to prevent multiple calls
   const handleClose = useCallback(() => {
      if (!closeCalledRef.current) {
         closeCalledRef.current = true;
         onClose();
      }
   }, [onClose]);

   // Multiple ESC key handlers for maximum compatibility
   useEffect(() => {
      // Handler 1: Capture phase on document
      const handleEscKeyCapture = (event) => {
         if (event.key === 'Escape' || event.keyCode === 27) {
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();
            handleClose();
         }
      };

      // Handler 2: Bubble phase on document
      const handleEscKeyBubble = (event) => {
         if (event.key === 'Escape' || event.keyCode === 27) {
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();
            handleClose();
         }
      };

      // Handler 3: Window level capture
      const handleWindowEsc = (event) => {
         if (event.key === 'Escape' || event.keyCode === 27) {
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();
            handleClose();
         }
      };

      // Handler 4: Modal ref keydown
      const handleModalKeyDown = (event) => {
         if (event.key === 'Escape' || event.keyCode === 27) {
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();
            handleClose();
         }
      };

      // Add all listeners
      document.addEventListener('keydown', handleEscKeyCapture, true);
      document.addEventListener('keydown', handleEscKeyBubble, false);
      window.addEventListener('keydown', handleWindowEsc, true);

      if (modalRef.current) {
         modalRef.current.addEventListener('keydown', handleModalKeyDown);
         modalRef.current.focus();
      }

      // Cleanup all listeners
      return () => {
         document.removeEventListener('keydown', handleEscKeyCapture, true);
         document.removeEventListener('keydown', handleEscKeyBubble, false);
         window.removeEventListener('keydown', handleWindowEsc, true);
         if (modalRef.current) {
            modalRef.current.removeEventListener('keydown', handleModalKeyDown);
         }
         closeCalledRef.current = false;
      };
   }, [handleClose]);

   // Prevent body scroll when modal is open
   useEffect(() => {
      const originalStyle = window.getComputedStyle(document.body).overflow;
      document.body.style.overflow = 'hidden';

      return () => {
         document.body.style.overflow = originalStyle;
      };
   }, []);

   // Close modal when clicking on backdrop
   const handleBackdropClick = (event) => {
      if (event.target === event.currentTarget) {
         handleClose();
      }
   };

   // Format date to readable format
   const formatDate = (dateString) => {
      const date = new Date(dateString);
      const now = new Date();
      const diffInMs = now - date;
      const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
      const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

      if (diffInHours < 1) {
         return 'Just now';
      } else if (diffInHours < 24) {
         return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
      } else if (diffInDays < 7) {
         return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
      } else {
         return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
         });
      }
   };

   // Calculate total pages
   const totalPages = Paginationdata?.total ? Math.ceil(Paginationdata.total / limit) : 1;

   // Handle page change
   const handlePageChange = (newPage) => {
      if (newPage >= 1 && newPage <= totalPages) {
         setCurrentPage(newPage);
      }
   };

   // Calculate showing range
   const startIndex = (currentPage - 1) * limit + 1;
   const endIndex = Math.min(currentPage * limit, Paginationdata?.total || NotificationData?.length || 0);

   return (
      <div
         className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[99999]"
         onClick={handleBackdropClick}
         role="dialog"
         aria-modal="true"
         aria-labelledby="notification-modal-title"
         style={{ isolation: 'isolate' }}>
         <div
            ref={modalRef}
            tabIndex={0}
            className={`rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] flex flex-col overflow-hidden outline-none ${
               isDark ? 'bg-gray-900' : 'bg-white'
            }`}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
               if (e.key === 'Escape' || e.keyCode === 27) {
                  e.preventDefault();
                  e.stopPropagation();
                  handleClose();
               }
            }}>
            {/* Modal Header with Gradient */}
            <div
               className={`relative px-6 py-5 ${
                  isDark
                     ? 'bg-gradient-to-r from-slate-800 to-slate-900 border-b border-slate-700'
                     : 'bg-gradient-to-r from-teal-600 to-sky-700 border-b border-indigo-500/20'
               }`}>
               <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                     <div className={`p-2 rounded-xl ${isDark ? 'bg-slate-700/50' : 'bg-white/20'}`}>
                        <Bell className={isDark ? 'text-blue-400' : 'text-white'} size={24} />
                     </div>
                     <h2
                        id="notification-modal-title"
                        className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-white'}`}>
                        Notifications
                     </h2>
                  </div>
                  <button
                     onClick={handleClose}
                     aria-label="Close notification modal"
                     className={`rounded-xl p-2 transition-all ${
                        isDark
                           ? 'hover:bg-slate-700/50 text-gray-300 hover:text-white'
                           : 'hover:bg-white/20 text-white hover:text-white'
                     }`}>
                     <X size={24} />
                  </button>
               </div>
            </div>

            {/* Modal Body with Cards */}
            <div className={`overflow-auto p-6 space-y-3 ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
               {NotificationData && NotificationData.length > 0 ? (
                  NotificationData.map((notification) => (
                     <div
                        key={notification.id}
                        className={`rounded-xl p-5 transition-all duration-200 cursor-pointer group ${
                           isDark
                              ? notification.isRead
                                 ? 'bg-gray-800/50 border border-gray-700/50 hover:border-gray-600 hover:bg-gray-800'
                                 : 'bg-gradient-to-br from-blue-900/30 to-indigo-900/20 border border-blue-700/50 hover:border-blue-600 shadow-lg shadow-blue-900/20'
                              : notification.isRead
                              ? 'bg-white border border-gray-200 hover:border-gray-300 hover:shadow-md'
                              : 'bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-300/60 hover:border-blue-400 shadow-md hover:shadow-lg'
                        }`}>
                        <div className="flex justify-between items-start gap-4">
                           <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                 <h3 className={`font-semibold text-lg ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                    {notification.title}
                                 </h3>
                                 {!notification.isRead && (
                                    <span
                                       className={`w-2.5 h-2.5 rounded-full animate-pulse ${
                                          isDark ? 'bg-blue-400' : 'bg-blue-600'
                                       }`}></span>
                                 )}
                              </div>
                              <p
                                 className={`text-sm mb-3 leading-relaxed ${
                                    isDark ? 'text-gray-300' : 'text-gray-600'
                                 }`}>
                                 {notification.subTitle}
                              </p>
                              <div
                                 className={`flex items-center gap-1.5 text-xs ${
                                    isDark ? 'text-gray-400' : 'text-gray-500'
                                 }`}>
                                 <Clock size={14} className={isDark ? 'text-gray-500' : 'text-gray-400'} />
                                 <span className="font-medium">{formatDate(notification.createdAt)}</span>
                              </div>
                           </div>
                        </div>
                     </div>
                  ))
               ) : (
                  <div className="text-center py-16">
                     <div className={`inline-flex p-6 rounded-full mb-4 ${isDark ? 'bg-gray-800/50' : 'bg-gray-100'}`}>
                        <Bell className={isDark ? 'text-gray-600' : 'text-gray-300'} size={48} />
                     </div>
                     <p className={`text-lg font-semibold mb-2 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                        No notifications yet
                     </p>
                     <p className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>You're all caught up! 🎉</p>
                  </div>
               )}
            </div>

            {/* Modal Footer with Pagination */}
            {Paginationdata && NotificationData && NotificationData.length > 0 && (
               <div
                  className={`flex justify-between items-center px-6 py-4 border-t ${
                     isDark ? 'bg-gray-800/50 border-gray-700' : 'bg-white border-gray-200'
                  }`}>
                  <p className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                     Showing{' '}
                     <span className={isDark ? 'text-blue-400' : 'text-blue-600'}>
                        {startIndex}-{endIndex}
                     </span>{' '}
                     of{' '}
                     <span className={isDark ? 'text-blue-400' : 'text-blue-600'}>
                        {Paginationdata.total || NotificationData.length}
                     </span>{' '}
                     notifications
                  </p>
                  <div className="flex items-center gap-2">
                     <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className={`p-2 rounded-lg transition-all ${
                           currentPage === 1
                              ? isDark
                                 ? 'text-gray-600 cursor-not-allowed'
                                 : 'text-gray-300 cursor-not-allowed'
                              : isDark
                              ? 'text-gray-300 hover:bg-gray-700 hover:text-white'
                              : 'text-gray-700 hover:bg-gray-100'
                        }`}>
                        <ChevronLeft size={20} />
                     </button>
                     <span
                        className={`text-sm font-medium px-4 py-1 rounded-lg ${
                           isDark ? 'text-gray-300 bg-gray-700/50' : 'text-gray-700 bg-gray-100'
                        }`}>
                        Page {currentPage} of {totalPages}
                     </span>
                     <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className={`p-2 rounded-lg transition-all ${
                           currentPage === totalPages
                              ? isDark
                                 ? 'text-gray-600 cursor-not-allowed'
                                 : 'text-gray-300 cursor-not-allowed'
                              : isDark
                              ? 'text-gray-300 hover:bg-gray-700 hover:text-white'
                              : 'text-gray-700 hover:bg-gray-100'
                        }`}>
                        <ChevronRight size={20} />
                     </button>
                     <button
                        onClick={handleClose}
                        className={`ml-4 px-6 py-2.5 rounded-lg transition-all font-semibold shadow-lg ${
                           isDark
                              ? 'bg-gradient-to-r from-sky-600 to-cyan-600 hover:from-sky-500 hover:to-cyan-500 text-white'
                              : 'bg-gradient-to-r from-teal-600 to-sky-600 hover:from-teal-700 hover:to-sky-700 text-white'
                        }`}>
                        Close
                     </button>
                  </div>
               </div>
            )}
         </div>
      </div>
   );
};

export default NotificationModal;
